import React, {useEffect, useState} from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Box, Grid, Checkbox, Card, CardMedia, SelectChangeEvent, Typography} from "@mui/material";
import axiosInstance from "../../../queries/axiosInstance";
import VideoJSPlayer from "../VideoJSComponent/VideoJSPlayer";
import StyledPopup from "../StyledPopup/StyledPopup";
import SpinnerBox from "../SpinnerBox/SpinnerBox";
import ErrorSnackBar from "../ErrorSnackBar/ErrorSnackBar";
import StyledDropdown from "../StyledDropdown/StyledDropdown";
import {useTheme} from "../../../contexts/theme";
import {Competition} from "./OpenerCloserComponent";
import {OpenerCloserObject} from "../../../types/opernerCloserTypes";

export const defaultOpenerCloserObject: OpenerCloserObject = {
    id: "",
    name: "",
    type: "",
    thumbnail_url: "",
    video_url: "",
    multi_event: false,
};

interface OpenerCloserContentProps {
    type: "opener" | "closer";
    isOpen: string | boolean;
    onClick: (e: React.SyntheticEvent, expanded: boolean) => void;
    selectedOpener?: OpenerCloserObject;
    setSelectedOpener?: (opener: OpenerCloserObject) => void;
    selectedCloser?: OpenerCloserObject;
    setSelectedCloser?:(closer: OpenerCloserObject) => void;
    multi_event: boolean;
    selectedItemsCompetitions: Competition[];
    selectedCompetition: any;
    setSelectedCompetition: any;
    need_dropdown?: boolean;
    isCollapseOpen: boolean;
    dynamicCompetitions: boolean; // in order to understand if the competition changed and to understand if is necessary to fetch data again
}

const OpenerCloserContent = ({
                                 type,
                                 selectedOpener,
                                 setSelectedOpener,
                                 selectedCloser,
                                 setSelectedCloser,
                                 multi_event,
                                 selectedItemsCompetitions,
                                 selectedCompetition,
                                 setSelectedCompetition,
                                 need_dropdown,
                                 isCollapseOpen,
                                 dynamicCompetitions
                             }: OpenerCloserContentProps) => {
    const theme = useTheme();
    const [openerCloserData, setOpenerCloserData] = useState<OpenerCloserObject[]>([]);
    const [filteredData, setFilteredData] = useState<OpenerCloserObject[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [openThumbnailPreview, setOpenThumbnailPreview] = useState(false);
    const [videoPlayerUrl, setVideoPlayerUrl] = useState("");
    const [loadError, setLoadError] = useState<{ error: boolean; message: string }>({error: false, message: ""});

    async function getOpenerCloserData() {
        setIsLoading(true);
        axiosInstance.post(`/highlight/openercloser`, {
            opener: type === "opener",
            closer: type === "closer",
            competition: selectedCompetition.id,
        })
            .then(({data}) => {
                setOpenerCloserData(data);
                setFilteredData(data);
            })
            .catch(() => setLoadError({error: true, message: "Error loading opener/closer data."}))
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        if(selectedCompetition && !dynamicCompetitions) {
            getOpenerCloserData();
        }
    }, [selectedCompetition]);

    useEffect(() => {
        if(isCollapseOpen) {
            getOpenerCloserData();
        }
    }, [isCollapseOpen]);

    useEffect(() => {
        if (!openerCloserData.length) return;
        setFilteredData(openerCloserData.filter(d => d.multi_event === multi_event));
    }, [openerCloserData]);

    const handleToggle = (data: OpenerCloserObject, checked: boolean) => {
        if (type === "opener" && setSelectedOpener) setSelectedOpener(checked ? data : defaultOpenerCloserObject);
        if (type === "closer" && setSelectedCloser) setSelectedCloser(checked ? data : defaultOpenerCloserObject);
    };

    const handleCompetitionChange = (e: SelectChangeEvent<string>) => {
        const found = selectedItemsCompetitions.find(competition => competition.id === e.target.value);
        if (found) setSelectedCompetition(found);
    };

    // Shared content (either accordion details or direct render)
    const content = isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <SpinnerBox height="100%"/>
        </Box>
    ) : (
        <Grid container spacing={2} sx={{maxHeight: 400, overflowY: "auto"}}>
            {filteredData.map(data => (
                <Grid item xs={4} key={data.id}>
                    <Card sx={{position: "relative"}}>
                        <Checkbox
                            sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                zIndex: 1,
                                borderRadius: "50%",
                                color: "white"
                            }}
                            checked={type === "opener" ? selectedOpener?.id === data.id : selectedCloser?.id === data.id}
                            onChange={e => handleToggle(data, e.target.checked)}
                        />
                        <CardMedia
                            component="img"
                            image={data.thumbnail_url}
                            alt={data.name}
                            onClick={() => {
                                setVideoPlayerUrl(data.video_url);
                                setOpenThumbnailPreview(true);
                            }}
                        />
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Box>
            <Box>
                <Box display={'flex'} justifyContent={'flex-start'} alignItems={'center'}>
                    <Box sx={{
                        color: theme.palette.white,
                        width: '150px',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        {type === "opener" ? "Select the opener" : "Select the closer"}
                    </Box>
                    <Box sx={{marginTop: theme.spacing.sm, marginLeft: theme.spacing.sm, width: '300px'}} onClick={(e: any) => e.stopPropagation()}>
                        {need_dropdown && selectedItemsCompetitions.length > 1 && (
                            <StyledDropdown
                                fullWidth={true}
                                options={selectedItemsCompetitions}
                                label={"Select the competition"}
                                displayField={'name'}
                                valueField={'id'}
                                value={selectedCompetition}
                                id={"competition-dropdown"}
                                onChange={handleCompetitionChange}
                            />
                        )}
                    </Box>
                </Box>
                <Box sx={{bgcolor: theme.palette.blue, p: 2, borderRadius: theme.border.radius.md}}>
                    {content}
                </Box>

                <StyledPopup isOpen={openThumbnailPreview} onClose={() => setOpenThumbnailPreview(false)}
                             width="calc((87vh/9)*16)" height="92vh">
                    <VideoJSPlayer url={videoPlayerUrl} sourceType={'video/mp4'}/>
                </StyledPopup>
                <ErrorSnackBar open={loadError.error} errorMessage={loadError.message}
                               setClose={() => setLoadError({error: false, message: ""})} autoHideDuration={5000}/>
            </Box>
        </Box>
    );
};

export default OpenerCloserContent;